import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader, Globe, Folder } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Языковые настройки
const LANGUAGES = {
  ru: { name: 'Русский', flag: '🇷🇺' },
  en: { name: 'English', flag: '🇬🇧' },
  kk: { name: 'Қазақша', flag: '🇰🇿' }
};

// Переводы интерфейса
const UI_TRANSLATIONS = {
  ru: {
    backToThesaurus: 'Вернуться к тезаурусу',
    categoryId: 'ID категории',
    otherLanguages: 'Другие языки',
    definition: 'Определение',
    scopeNote: 'Примечание',
    useFor: 'Используйте вместо',
    narrowerTerms: 'Узкие термины',
    relatedTerms: 'Связанные термины',
    usedFor: 'Используется для',
    lexicalEquivalent: 'Лексические эквиваленты',
    preferredTerm: 'Предпочтительный термин',
    termsInCategory: 'Термины в этой категории',
    notFound: 'Категория не найдена',
    noDefinition: 'Определение не указано'
  },
  en: {
    backToThesaurus: 'Back to thesaurus',
    categoryId: 'Category ID',
    otherLanguages: 'Other languages',
    definition: 'Definition',
    scopeNote: 'Scope Note',
    useFor: 'Use For',
    narrowerTerms: 'Narrower Terms',
    relatedTerms: 'Related Terms',
    usedFor: 'Used For',
    lexicalEquivalent: 'Lexical Equivalents',
    preferredTerm: 'Preferred Term',
    termsInCategory: 'Terms in this category',
    notFound: 'Category not found',
    noDefinition: 'No definition provided'
  },
  kk: {
    backToThesaurus: 'Тезаурусқа оралу',
    categoryId: 'Санат идентификаторы',
    otherLanguages: 'Басқа тілдер',
    definition: 'Анықтама',
    scopeNote: 'Ескертпе',
    useFor: 'Орнына қолданылады',
    narrowerTerms: 'Тар терминдер',
    relatedTerms: 'Байланысты терминдер',
    usedFor: 'Үшін қолданылады',
    lexicalEquivalent: 'Лексикалық эквиваленттер',
    preferredTerm: 'Қолайлы термин',
    termsInCategory: 'Осы санаттағы терминдер',
    notFound: 'Санат табылмады',
    noDefinition: 'Анықтама көрсетілмеген'
  }
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('ru');

  const t = UI_TRANSLATIONS[language];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catResponse, termsResponse] = await Promise.all([
          axios.get(`${API}/categories/${categoryId}`),
          axios.get(`${API}/thesaurus`)
        ]);
        setCategory(catResponse.data);
        // Фильтруем термины по категории
        const categoryTerms = termsResponse.data.filter(
          term => term.category === catResponse.data.name
        );
        setTerms(categoryTerms);
      } catch (err) {
        console.error('Error fetching category:', err);
        setError(t.notFound);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId, t.notFound]);

  // Получить название на выбранном языке
  const getCategoryName = () => {
    if (!category) return '';
    if (language === 'en' && category.name_en) return category.name_en;
    if (language === 'kk' && category.name_kk) return category.name_kk;
    return category.name;
  };

  // Получить определение на выбранном языке
  const getDefinition = () => {
    if (!category) return null;
    if (language === 'en' && category.definition_en) return category.definition_en;
    if (language === 'kk' && category.definition_kk) return category.definition_kk;
    return category.definition;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || t.notFound}</p>
          <button
            onClick={() => navigate('/thesaurus')}
            className="text-blue-600 hover:underline"
          >
            {t.backToThesaurus}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with language selector */}
      <div className="border-b border-gray-300">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/thesaurus')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToThesaurus}
            </button>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
              {Object.entries(LANGUAGES).map(([code, lang]) => (
                <button
                  key={code}
                  onClick={() => setLanguage(code)}
                  className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
                    language === code
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="hidden sm:inline">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        {/* Category badge */}
        <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
          <Folder className="w-4 h-4" />
          <span>Категория / Category</span>
        </div>

        {/* Category Name */}
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          {getCategoryName()}
        </h1>

        {/* Other Languages */}
        <div className="mb-8 bg-gray-50 border-l-4 border-blue-500 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t.otherLanguages}
          </h2>
          <div className="space-y-2">
            {Object.entries(LANGUAGES).map(([code, lang]) => {
              if (code === language) return null;
              const nameInLang = code === 'en' ? category.name_en : 
                                 code === 'kk' ? category.name_kk : category.name;
              if (!nameInLang) return null;
              return (
                <div key={code} className="flex gap-3">
                  <span className="font-semibold text-gray-700 w-32 flex items-center gap-2">
                    <span>{lang.flag}</span> {lang.name}:
                  </span>
                  <span className="text-gray-900">{nameInLang}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Definition */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">{t.definition}</h2>
          {getDefinition() ? (
            <p className="text-gray-800 text-lg leading-relaxed border-l-4 border-gray-300 pl-4">
              {getDefinition()}
            </p>
          ) : (
            <p className="text-gray-500 italic border-l-4 border-gray-200 pl-4">
              {t.noDefinition}
            </p>
          )}
        </div>

        {/* Scope Note */}
        {category.scope_note && (
          <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <h2 className="text-lg font-bold text-gray-900 mb-2">{t.scopeNote}</h2>
            <p className="text-gray-800">{category.scope_note}</p>
          </div>
        )}

        {/* UF - Use For (Synonyms) */}
        {category.use_for && category.use_for.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-blue-600">UF</span> — {t.useFor}
            </h2>
            <div className="pl-6 space-y-2">
              {category.use_for.map((uf, index) => (
                <div key={index} className="text-gray-800">
                  • {uf}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NT - Narrower Terms */}
        {category.narrower_terms && category.narrower_terms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-purple-600">NT</span> — {t.narrowerTerms}
            </h2>
            <div className="pl-6 space-y-2">
              {category.narrower_terms.map((nt, index) => (
                <div key={index} className="text-gray-800">
                  • {nt}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RT - Related Terms */}
        {category.related_terms && category.related_terms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-orange-600">RT</span> — {t.relatedTerms}
            </h2>
            <div className="pl-6 space-y-2">
              {category.related_terms.map((rt, index) => (
                <div key={index} className="text-gray-800">
                  • {rt}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms in Category */}
        {terms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t.termsInCategory} ({terms.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {terms.map((term) => (
                <Link
                  key={term.id}
                  to={`/concept/${term.id}`}
                  className="p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                >
                  • {term.term || term.pt?.ru || 'Без названия'}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
